<template>
  <div class="relative overflow-hidden">
    <div class="overflow-hidden">
      <div
        v-if="shouldShowPrevButton"
        class="absolute left-0 top-[-1px] z-[1] hidden h-[42px] w-[104px] items-center justify-start md:flex"
        :class="$style.gradientPrevButton"
      >
        <RevButtonIcon
          class="rounded-full border-1"
          :icon="IconChevronLeft"
          variant="secondary"
          @click="onPrevClick"
        />
      </div>

      <div ref="wrapper" class="flex flex-row gap-16">
        <slot />
      </div>

      <div
        v-if="shouldShowNextButton"
        class="absolute right-0 top-[-1px] z-[1] hidden h-[42px] w-[104px] items-center justify-end md:flex"
        :class="$style.gradientNextButton"
      >
        <RevButtonIcon
          class="rounded-full border-1"
          :icon="IconChevronRight"
          variant="secondary"
          @click="onNextClick"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'

import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { IconChevronLeft } from '@ds/icons/IconChevronLeft'
import { IconChevronRight } from '@ds/icons/IconChevronRight'

const props = defineProps<{ length: number }>()

const wrapper = ref<HTMLElement | null>()
const displayedButton = ref(0)
const shouldShowPrevButton = ref(false)
const shouldShowNextButton = ref(false)

function updateShouldDisplayPrevButton() {
  if (wrapper.value) {
    shouldShowPrevButton.value =
      wrapper.value.scrollWidth > wrapper.value.offsetWidth &&
      displayedButton.value > 0
  }
}

function updateShouldDisplayNextButton() {
  const itemsLength = wrapper.value?.children.length || 0

  if (wrapper.value && itemsLength) {
    shouldShowNextButton.value =
      wrapper.value.scrollWidth > wrapper.value.offsetWidth &&
      displayedButton.value < itemsLength - 1
  }
}

function scrollTo(index: number) {
  if (wrapper.value) {
    const items = Array.from(wrapper.value.children)

    items[index].scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start',
    })
    displayedButton.value = index
  }

  updateShouldDisplayPrevButton()
  updateShouldDisplayNextButton()
}

function onPrevClick() {
  scrollTo(displayedButton.value - 1)
}

function onNextClick() {
  scrollTo(displayedButton.value + 1)
}

onMounted(() => {
  window.addEventListener('resize', updateShouldDisplayPrevButton)
  window.addEventListener('resize', updateShouldDisplayNextButton)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateShouldDisplayPrevButton)
  window.removeEventListener('resize', updateShouldDisplayNextButton)
})

watch(
  () => props.length,
  () => {
    updateShouldDisplayNextButton()
  },
  { immediate: true },
)
</script>

<style module>
.gradientPrevButton {
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 1) 0%,
    rgba(255, 255, 255, 1) 40%,
    rgba(255, 255, 255, 0) 100%
  );
}

.gradientNextButton {
  background: linear-gradient(
    to left,
    rgba(255, 255, 255, 1) 0%,
    rgba(255, 255, 255, 1) 40%,
    rgba(255, 255, 255, 0) 100%
  );
}
</style>
